import { useState, useCallback } from 'react'
import { AssetManagerService } from 'utility/services/asset-manager'
import { useAuth } from 'context/user'
import { useNotification } from './toast'

const pickList = (response) => {
    const payload = response?.data || response
    if (Array.isArray(payload)) return payload
    if (Array.isArray(payload?.Data)) return payload.Data
    if (Array.isArray(payload?.Items)) return payload.Items
    return []
}

const pickOne = (response) => {
    const payload = response?.data || response
    if (payload?.Data && !Array.isArray(payload.Data)) return payload.Data
    return payload
}

const pickMutationResult = response => {
    const payload = pickOne(response)
    if (
        payload === undefined ||
        payload === null ||
        payload === ''
    ) {
        return true
    }
    return payload
}

const pickMutationId = (result, key) => {
    if (result && typeof result === 'object') {
        return result[key] || 0
    }
    const numericValue = Number(result)
    return Number.isNaN(numericValue) ? 0 : numericValue
}

// Hook for Category operations
export const useAssetCategory = () => {
    const { user } = useAuth()
    const { showError, showSuccess } = useNotification()
    const [loading, setLoading] = useState(false)
    const [categories, setCategories] = useState([])

    const fetchCategories = useCallback(async (onlyActive = true) => {
        if (!user?.CompanyId) return
        setLoading(true)
        try {
            const response = await AssetManagerService.categoryList({
                CompanyId: user.CompanyId,
                OnlyActive: onlyActive,
            })
            setCategories(pickList(response))
        } catch (error) {
            console.error('Error fetching categories:', error)
            showError(error?.error?.message || 'Failed to fetch categories')
        } finally {
            setLoading(false)
        }
    }, [user?.CompanyId, showError])

    const getCategory = useCallback(async (id) => {
        if (!id) return null
        try {
            const response = await AssetManagerService.categoryGet({ Id: id })
            return pickOne(response)
        } catch (error) {
            console.error('Error fetching category:', error)
            showError(error?.error?.message || 'Failed to fetch category')
            return null
        }
    }, [showError])

    const saveCategory = useCallback(async (categoryData) => {
        if (!user?.CompanyId) return null
        try {
            const response = await AssetManagerService.categorySave({
                Category: {
                    ...categoryData,
                    CompanyId: user.CompanyId,
                },
            })
            const result = pickMutationResult(response)
            const nextCategory = {
                ...categoryData,
                ...(typeof result === 'object' ? result : {}),
                AssetCategoryId:
                    categoryData.AssetCategoryId ||
                    result?.AssetCategoryId ||
                    Date.now(),
                CompanyId: user.CompanyId,
            }
            setCategories(prev => {
                const existingIndex = prev.findIndex(
                    item =>
                        Number(item.AssetCategoryId || item.id) ===
                        Number(nextCategory.AssetCategoryId),
                )
                if (existingIndex >= 0) {
                    return prev.map((item, index) =>
                        index === existingIndex
                            ? { ...item, ...nextCategory }
                            : item,
                    )
                }
                return [nextCategory, ...prev]
            })
            showSuccess('Category saved successfully')
            return result === false ? true : result
        } catch (error) {
            console.error('Error saving category:', error)
            showError(error?.error?.message || 'Failed to save category')
            return null
        }
    }, [user?.CompanyId, showError, showSuccess])

    const deleteCategory = useCallback(async (id) => {
        if (!id) return false
        try {
            await AssetManagerService.categoryDelete({ Id: id })
            setCategories(prev =>
                prev.filter(
                    item => Number(item.AssetCategoryId || item.id) !== Number(id),
                ),
            )
            showSuccess('Category deleted successfully')
            return true
        } catch (error) {
            console.error('Error deleting category:', error)
            showError(error?.error?.message || 'Failed to delete category')
            return false
        }
    }, [showError, showSuccess])

    return {
        categories,
        loading,
        fetchCategories,
        getCategory,
        saveCategory,
        deleteCategory,
    }
}

// Hook for Location operations
export const useAssetLocation = () => {
    const { user } = useAuth()
    const { showError, showSuccess } = useNotification()
    const [loading, setLoading] = useState(false)
    const [locations, setLocations] = useState([])

    const fetchLocations = useCallback(async (onlyActive = true) => {
        if (!user?.CompanyId) return
        setLoading(true)
        try {
            const response = await AssetManagerService.locationList({
                CompanyId: user.CompanyId,
                OnlyActive: onlyActive,
            })
            setLocations(pickList(response))
        } catch (error) {
            console.error('Error fetching locations:', error)
            showError(error?.error?.message || 'Failed to fetch locations')
        } finally {
            setLoading(false)
        }
    }, [user?.CompanyId, showError])

    const getLocation = useCallback(async (id) => {
        if (!id) return null
        try {
            const response = await AssetManagerService.locationGet({ Id: id })
            return pickOne(response)
        } catch (error) {
            console.error('Error fetching location:', error)
            showError(error?.error?.message || 'Failed to fetch location')
            return null
        }
    }, [showError])

    const saveLocation = useCallback(async (locationData) => {
        if (!user?.CompanyId) return null
        try {
            const response = await AssetManagerService.locationSave({
                Location: {
                    ...locationData,
                    CompanyId: user.CompanyId,
                },
            })
            const result = pickMutationResult(response)
            const nextLocation = {
                ...locationData,
                ...(typeof result === 'object' ? result : {}),
                AssetLocationId:
                    locationData.AssetLocationId ||
                    result?.AssetLocationId ||
                    Date.now(),
                CompanyId: user.CompanyId,
            }
            setLocations(prev => {
                const existingIndex = prev.findIndex(
                    item =>
                        Number(item.AssetLocationId || item.id) ===
                        Number(nextLocation.AssetLocationId),
                )
                if (existingIndex >= 0) {
                    return prev.map((item, index) =>
                        index === existingIndex
                            ? { ...item, ...nextLocation }
                            : item,
                    )
                }
                return [nextLocation, ...prev]
            })
            showSuccess('Location saved successfully')
            return result === false ? true : result
        } catch (error) {
            console.error('Error saving location:', error)
            showError(error?.error?.message || 'Failed to save location')
            return null
        }
    }, [user?.CompanyId, showError, showSuccess])

    const deleteLocation = useCallback(async (id) => {
        if (!id) return false
        try {
            await AssetManagerService.locationDelete({ Id: id })
            showSuccess('Location deleted successfully')
            return true
        } catch (error) {
            console.error('Error deleting location:', error)
            showError(error?.error?.message || 'Failed to delete location')
            return false
        }
    }, [showError, showSuccess])

    return {
        locations,
        loading,
        fetchLocations,
        getLocation,
        saveLocation,
        deleteLocation,
    }
}

// Hook for Asset operations
export const useAsset = () => {
    const { user } = useAuth()
    const { showError, showSuccess } = useNotification()
    const [loading, setLoading] = useState(false)
    const [assets, setAssets] = useState([])
    const [totalCount, setTotalCount] = useState(0)

    const fetchAssets = useCallback(async (filters = {}) => {
        if (!user?.CompanyId) return
        setLoading(true)
        try {
            const response = await AssetManagerService.assetList({
                CompanyId: user.CompanyId,
                OnlyActive: filters.OnlyActive !== false,
                ...filters,
            })
            const data = pickList(response)
            setAssets(data)
            setTotalCount(data.length)
        } catch (error) {
            console.error('Error fetching assets:', error)
            showError(error?.error?.message || 'Failed to fetch assets')
            setAssets([])
            setTotalCount(0)
        } finally {
            setLoading(false)
        }
    }, [user?.CompanyId, showError])

    const getAsset = useCallback(async (id) => {
        if (!id) return null
        try {
            const response = await AssetManagerService.assetGet({ Id: id })
            return pickOne(response)
        } catch (error) {
            console.error('Error fetching asset:', error)
            showError(error?.error?.message || 'Failed to fetch asset')
            return null
        }
    }, [showError])

    const saveAsset = useCallback(async (assetData) => {
        if (!user?.CompanyId) return null
        try {
            const response = await AssetManagerService.assetSave({
                Asset: {
                    ...assetData,
                    CompanyId: user.CompanyId,
                },
            })
            const result = pickMutationResult(response)
            const savedAssetId = assetData.AssetId || pickMutationId(result, 'AssetId')
            const nextAsset = {
                ...assetData,
                ...(typeof result === 'object' ? result : {}),
                AssetId: savedAssetId || Date.now(),
                CompanyId: user.CompanyId,
            }
            setAssets(prev => {
                const existingIndex = prev.findIndex(
                    item =>
                        Number(item.AssetId || item.id) ===
                        Number(nextAsset.AssetId),
                )
                if (existingIndex >= 0) {
                    return prev.map((item, index) =>
                        index === existingIndex
                            ? { ...item, ...nextAsset }
                            : item,
                    )
                }
                return [nextAsset, ...prev]
            })
            setTotalCount(prev => {
                const alreadyExists = assets.some(
                    item =>
                        Number(item.AssetId || item.id) ===
                        Number(nextAsset.AssetId),
                )
                return alreadyExists ? prev : prev + 1
            })
            showSuccess('Asset saved successfully')
            return result === false ? true : result
        } catch (error) {
            console.error('Error saving asset:', error)
            showError(error?.error?.message || 'Failed to save asset')
            return null
        }
    }, [assets, user?.CompanyId, showError, showSuccess])

    const deleteAsset = useCallback(async (id) => {
        if (!id) return false
        try {
            await AssetManagerService.assetDelete({ Id: id })
            setAssets(prev =>
                prev.filter(item => Number(item.AssetId || item.id) !== Number(id)),
            )
            setTotalCount(prev => Math.max(0, prev - 1))
            showSuccess('Asset deleted successfully')
            return true
        } catch (error) {
            console.error('Error deleting asset:', error)
            showError(error?.error?.message || 'Failed to delete asset')
            return false
        }
    }, [showError, showSuccess])

    return {
        assets,
        totalCount,
        loading,
        fetchAssets,
        getAsset,
        saveAsset,
        deleteAsset,
    }
}

// Hook for Inspection operations
export const useAssetInspection = () => {
    const { user } = useAuth()
    const { showError, showSuccess } = useNotification()
    const [loading, setLoading] = useState(false)
    const [inspections, setInspections] = useState([])

    const getInspectionsByAsset = useCallback(async (assetId, onlyActive = true) => {
        if (!user?.CompanyId || !assetId) return []
        try {
            const response = await AssetManagerService.inspectionListByAsset({
                CompanyId: user.CompanyId,
                AssetId: assetId,
                OnlyActive: onlyActive,
            })
            return pickList(response)
        } catch (error) {
            console.error('Error fetching inspections:', error)
            showError(error?.error?.message || 'Failed to fetch inspections')
            return []
        }
    }, [user?.CompanyId, showError])

    const fetchInspectionsByAsset = useCallback(async (assetId, onlyActive = true) => {
        if (!user?.CompanyId || !assetId) return
        setLoading(true)
        try {
            const list = await getInspectionsByAsset(assetId, onlyActive)
            setInspections(list)
        } finally {
            setLoading(false)
        }
    }, [user?.CompanyId, getInspectionsByAsset])

    const getInspection = useCallback(async (id) => {
        if (!id) return null
        try {
            const response = await AssetManagerService.inspectionGet({ Id: id })
            return pickOne(response)
        } catch (error) {
            console.error('Error fetching inspection:', error)
            showError(error?.error?.message || 'Failed to fetch inspection')
            return null
        }
    }, [showError])

    const saveInspection = useCallback(async (inspectionData) => {
        if (!user?.CompanyId) return null
        try {
            const response = await AssetManagerService.inspectionSave({
                Inspection: {
                    ...inspectionData,
                    CompanyId: user.CompanyId,
                },
            })
            const result = pickMutationResult(response)
            const savedInspectionId =
                inspectionData.AssetInspectionId ||
                pickMutationId(result, 'AssetInspectionId') ||
                pickMutationId(result, 'Id')
            const nextInspection = {
                ...inspectionData,
                ...(typeof result === 'object' ? result : {}),
                AssetInspectionId: savedInspectionId || Date.now(),
                CompanyId: user.CompanyId,
            }
            setInspections(prev => {
                const existingIndex = prev.findIndex(
                    item =>
                        Number(item.AssetInspectionId || item.Id) ===
                        Number(nextInspection.AssetInspectionId),
                )
                if (existingIndex >= 0) {
                    return prev.map((item, index) =>
                        index === existingIndex
                            ? { ...item, ...nextInspection }
                            : item,
                    )
                }
                return [nextInspection, ...prev]
            })
            showSuccess('Inspection saved successfully')
            return result === false ? true : result
        } catch (error) {
            console.error('Error saving inspection:', error)
            showError(error?.error?.message || 'Failed to save inspection')
            return null
        }
    }, [user?.CompanyId, showError, showSuccess])

    const deleteInspection = useCallback(async (id) => {
        if (!id) return false
        try {
            await AssetManagerService.inspectionDelete({ Id: id })
            setInspections(prev =>
                prev.filter(
                    item =>
                        Number(item.AssetInspectionId || item.Id) !== Number(id),
                ),
            )
            showSuccess('Inspection deleted successfully')
            return true
        } catch (error) {
            console.error('Error deleting inspection:', error)
            showError(error?.error?.message || 'Failed to delete inspection')
            return false
        }
    }, [showError, showSuccess])

    return {
        inspections,
        loading,
        fetchInspectionsByAsset,
        getInspectionsByAsset,
        getInspection,
        saveInspection,
        deleteInspection,
    }
}

// Hook for Activity Log / Audit Trail
export const useAssetActivity = () => {
    const { showError } = useNotification()
    const [loading, setLoading] = useState(false)
    const [activities, setActivities] = useState([])

    const fetchActivities = useCallback(
        async () => {
            setLoading(true)
            try {
                // No backend audit-log API is available yet.
                setActivities([])
            } catch (error) {
                console.error('Error fetching asset activities:', error)
                showError(
                    error?.error?.message || 'Failed to fetch asset activities',
                )
                setActivities([])
            } finally {
                setLoading(false)
            }
        },
        [showError],
    )

    const captureActivity = useCallback(
        () => null,
        [],
    )

    return {
        activities,
        loading,
        fetchActivities,
        captureActivity,
    }
}

// Hook for Inspection Item operations
export const useAssetInspectionItem = () => {
    const { showError, showSuccess } = useNotification()
    const [loading, setLoading] = useState(false)
    const [items, setItems] = useState([])

    const fetchInspectionItems = useCallback(
        async (assetInspectionId, onlyActive = true) => {
            if (!assetInspectionId) return
            setLoading(true)
            try {
                const response = await AssetManagerService.inspectionItemList({
                    AssetInspectionId: assetInspectionId,
                    OnlyActive: onlyActive,
                })
                setItems(pickList(response))
            } catch (error) {
                console.error('Error fetching inspection items:', error)
                showError(
                    error?.error?.message || 'Failed to fetch inspection items',
                )
                setItems([])
            } finally {
                setLoading(false)
            }
        },
        [showError],
    )

    const saveInspectionItem = useCallback(
        async itemData => {
            try {
                const response = await AssetManagerService.inspectionItemSave({
                    Item: itemData,
                })
                showSuccess('Inspection item saved successfully')
                return pickMutationResult(response)
            } catch (error) {
                console.error('Error saving inspection item:', error)
                showError(
                    error?.error?.message || 'Failed to save inspection item',
                )
                return null
            }
        },
        [showError, showSuccess],
    )

    const deleteByInspection = useCallback(
        async id => {
            if (!id) return false
            try {
                await AssetManagerService.inspectionItemDeleteByInspection({ Id: id })
                showSuccess('Inspection items deleted successfully')
                return true
            } catch (error) {
                console.error('Error deleting inspection items:', error)
                showError(
                    error?.error?.message || 'Failed to delete inspection items',
                )
                return false
            }
        },
        [showError, showSuccess],
    )

    return {
        items,
        loading,
        fetchInspectionItems,
        saveInspectionItem,
        deleteByInspection,
    }
}

// Hook for Inspection Media operations
export const useAssetInspectionMedia = () => {
    const { showError, showSuccess } = useNotification()
    const [loading, setLoading] = useState(false)
    const [media, setMedia] = useState([])

    const fetchInspectionMedia = useCallback(
        async (assetInspectionId, onlyActive = true) => {
            if (!assetInspectionId) return
            setLoading(true)
            try {
                const response = await AssetManagerService.inspectionMediaList({
                    AssetInspectionId: assetInspectionId,
                    OnlyActive: onlyActive,
                })
                setMedia(pickList(response))
            } catch (error) {
                console.error('Error fetching inspection media:', error)
                showError(
                    error?.error?.message || 'Failed to fetch inspection media',
                )
                setMedia([])
            } finally {
                setLoading(false)
            }
        },
        [showError],
    )

    const saveInspectionMedia = useCallback(
        async mediaData => {
            try {
                const response = await AssetManagerService.inspectionMediaSave({
                    Media: mediaData,
                })
                showSuccess('Inspection media saved successfully')
                return pickMutationResult(response)
            } catch (error) {
                console.error('Error saving inspection media:', error)
                showError(
                    error?.error?.message || 'Failed to save inspection media',
                )
                return null
            }
        },
        [showError, showSuccess],
    )

    const deleteInspectionMedia = useCallback(
        async id => {
            if (!id) return false
            try {
                await AssetManagerService.inspectionMediaDelete({ Id: id })
                showSuccess('Inspection media deleted successfully')
                return true
            } catch (error) {
                console.error('Error deleting inspection media:', error)
                showError(
                    error?.error?.message || 'Failed to delete inspection media',
                )
                return false
            }
        },
        [showError, showSuccess],
    )

    return {
        media,
        loading,
        fetchInspectionMedia,
        saveInspectionMedia,
        deleteInspectionMedia,
    }
}
